/**
 * `/opportunities/:id` — a persistent header, tabs on the left, the numbers on
 * the right.
 *
 * Everything numeric on the rail is read from `v_opportunity_effective`, which
 * §5.8 makes the single definition of all of it — nothing here recomputes a
 * value, a probability, or a stall predicate.
 *
 * The **assigned FAE sees full write affordances** on this opportunity and
 * everything under it (§2 [R2]). That is the point of the FAE branch in the
 * scope predicate: an FAE who ran the design review must be able to log the
 * meeting, add the technical note, and close the action item. If the UI hid
 * them, the FAE would assume they could not help. `canWrite` comes from the
 * server, which computes it with the same shared function it enforces with.
 */

import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { CalendarPlus, ChevronRight, StickyNote, UserCog } from 'lucide-react';
import type { ClosureRow, EntityContext } from '@menlo/shared';
import {
  FOCUS_HELP_TEXT,
  FORECAST_CATEGORIES,
  PROGRAM_LIFE_OPTIONS,
  VERIFIED_HELP_TEXT,
} from '@menlo/shared';
import { useApiMutation, useApiQuery, type Row } from '@/lib/queries';
import { useCanChangeOpportunityStatus, useCanSetOpportunityFlags, useMe } from '@/auth/useMe';
import { useReferenceData, useStages } from '@/lib/reference';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { TabList, TabPanel, useTab, type TabDefinition } from '@/components/ui/tabs';
import { StageStepper } from '@/components/domain/StageStepper';
import { RealizationBadge, StatusBadge } from '@/components/domain/Chips';
import { DualBasisRevenue } from '@/components/domain/Money';
import { RiskPanel } from '@/components/domain/RiskBadges';
import { InlineEdit } from '@/features/opportunities/InlineEdit';
import { ProductLines } from '@/features/opportunities/ProductLines';
import { BuyingCommittee } from '@/features/opportunities/BuyingCommittee';
import { CloseDialog } from '@/features/opportunities/CloseDialog';
import { useStageMove } from '@/features/opportunities/useStageMove';
import { NotesPanel } from '@/features/notes/NotesPanel';
import { useNoteComposer } from '@/features/notes/useNoteComposer';
import { AttachmentsPanel } from '@/features/attachments/AttachmentsPanel';
import { ActionItemRow, useQuickAdd } from '@/features/actions/ActionItems';
import { MeetingDialog } from '@/features/meetings/MeetingDialog';
import { formatDate, formatDateTime, formatMoney, humanize } from '@/lib/format';

export function OpportunityDetail(): React.ReactElement {
  const { id = '' } = useParams();
  const { data: me } = useMe();
  const reference = useReferenceData();
  const { list: stages } = useStages();

  const context = useApiQuery<EntityContext>(['opportunity', id], `/api/opportunities/${id}`);
  const record = (context.data?.record ?? {}) as Row;
  const canWrite = context.data?.canWrite === true;
  const isManager = me?.tier === 'Manager' || me?.tier === 'Admin';
  const canChangeStatus = useCanChangeOpportunityStatus();
  const canSetFlags = useCanSetOpportunityFlags();

  const [closing, setClosing] = useState(false);
  const [assigningFae, setAssigningFae] = useState(false);
  const [loggingMeeting, setLoggingMeeting] = useState(false);
  const [notePrompt, setNotePrompt] = useState(false);
  const noteComposer = useNoteComposer();

  const mover = useStageMove(() => setNotePrompt(true));
  const quickAdd = useQuickAdd({
    opportunityId: id,
    accountId: (record['account_id'] as string | undefined) ?? null,
    label: `On ${String(record['opp_number'] ?? '')}`,
  });

  const save = useApiMutation<Record<string, unknown>, Record<string, unknown>>(
    (body) => ({ path: `/api/opportunities/${id}`, method: 'PUT', body }),
    { invalidate: [['opportunity', id]] },
  );

  const reopen = useApiMutation<Record<string, unknown>, void>(
    () => ({ path: `/api/opportunities/${id}/reopen`, method: 'POST', body: {} }),
    { invalidate: [['opportunity', id]] },
  );

  const closures = (context.data?.closures ?? []) as unknown as ClosureRow[];
  const openItems = (context.data?.openItems ?? []) as unknown as Row[];

  const tabs: TabDefinition[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'activity', label: 'Activity' },
    { id: 'actions', label: 'Actions', count: openItems.length },
    { id: 'files', label: 'Files', count: context.data?.attachments.length },
    { id: 'history', label: 'History' },
  ];
  const [tab, setTab] = useTab(tabs);

  // `Add note` in the header has to reach the composer inside `NotesPanel`,
  // which lives on the Overview tab and unmounts while another tab is showing.
  // So the composer's state is held here, where it survives that unmount, and
  // the panel only renders it — a cancel and a half-typed draft both stick.
  const addNote = (): void => {
    setTab('overview');
    noteComposer.requestOpen();
  };

  if (context.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (context.error !== null) {
    return (
      <p className="rounded-md border border-destructive/40 bg-red-50 p-3 text-sm text-red-900">
        {context.error.message}
      </p>
    );
  }

  const status = String(record['status']);
  const terminal = ['won', 'lost', 'no_decision', 'design_win_lapsed', 'invalid'].includes(status);
  /**
   * Decision 12: an Invalid deal is brought back — reopened, re-closed, or won
   * through a stage move — only by a Manager or an Admin. The same shared
   * predicate the API enforces with; for every other status it is `canWrite`.
   */
  const invalid = status === 'invalid';
  const mayLeaveStatus =
    canWrite &&
    canChangeStatus(
      {
        owner_user_id: (record['owner_user_id'] as string | null) ?? null,
        fae_user_id: (record['fae_user_id'] as string | null) ?? null,
        status,
      },
      'open',
    );

  return (
    <div className="space-y-4">
      {/* ------------------------------------------------------------- header */}
      <header className="space-y-3 rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-xs text-muted-foreground">
              {String(record['opp_number'] ?? '')}
            </p>
            <h1 className="text-lg font-semibold">{String(record['name'] ?? '')}</h1>
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Link to={`/accounts/${String(record['account_id'])}`} className="hover:underline">
                {String(record['account_name'] ?? '')}
              </Link>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <StatusBadge status={String(record['status'])} />
              <RealizationBadge status={String(record['realization_status'])} />
              <span>
                Owner {String(record['owner_name'] ?? '—')} · FAE{' '}
                {String(record['fae_name'] ?? 'unassigned')}
              </span>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canWrite && (!invalid || mayLeaveStatus) && (
              <Select
                aria-label="Advance stage"
                className="h-8 w-auto text-xs"
                value={String(record['stage_code'])}
                onChange={(event) => mover.move(record, event.target.value)}
              >
                {stages.map((stage) => (
                  <option key={stage.code} value={stage.code}>
                    Move to {stage.name}
                  </option>
                ))}
              </Select>
            )}
            {canWrite && (
              <Button size="sm" variant="outline" onClick={() => setLoggingMeeting(true)}>
                <CalendarPlus className="h-3.5 w-3.5" aria-hidden="true" />
                Log meeting
              </Button>
            )}
            {canWrite && quickAdd.button}
            {canWrite && (
              <Button size="sm" variant="outline" onClick={addNote}>
                <StickyNote className="h-3.5 w-3.5" aria-hidden="true" />
                Add note
              </Button>
            )}
            {/* §2: owner and FAE are settable only by a Manager or an Admin. */}
            {isManager && (
              <Button size="sm" variant="outline" onClick={() => setAssigningFae(true)}>
                <UserCog className="h-3.5 w-3.5" aria-hidden="true" />
                Assign FAE
              </Button>
            )}
            {canWrite && !terminal && (
              <Button size="sm" onClick={() => setClosing(true)}>
                Close
              </Button>
            )}
            {mayLeaveStatus && terminal && (
              <>
                <Button size="sm" variant="outline" onClick={() => reopen.mutate()}>
                  Reopen
                </Button>
                <Button size="sm" onClick={() => setClosing(true)}>
                  Close again
                </Button>
              </>
            )}
          </div>
        </div>

        {invalid && (
          <p className="rounded-md border border-amber-500/50 bg-amber-50 p-2 text-xs text-amber-950">
            Marked <strong>Invalid — not a real opportunity</strong>. It is kept, and left out of
            the list, the pipeline, the forecast and every report. The import skips it.
            {mayLeaveStatus ? '' : ' Only a Manager or an Admin can bring it back.'}
          </p>
        )}

        <StageStepper stageCode={String(record['stage_code'])} />

        <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2 text-sm">
          <span className="text-xs font-medium text-muted-foreground">Next step</span>
          <InlineEdit
            label="Next step"
            className="min-w-[16rem] flex-1"
            value={record['next_step'] as string}
            disabled={!canWrite}
            placeholder="What happens next?"
            onSave={(next) => save.mutateAsync({ next_step: next })}
          />
          <InlineEdit
            label="Next step due date"
            kind="date"
            value={record['next_step_due_date'] as string}
            disabled={!canWrite}
            onSave={(next) => save.mutateAsync({ next_step_due_date: next })}
          />
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/* --------------------------------------------------------- the tabs */}
        <div className="min-w-0">
          <TabList tabs={tabs} active={tab} onSelect={setTab} />

          <TabPanel id="overview" active={tab}>
            <div className="space-y-6">
              <NotesPanel
                entityType="opportunity"
                entityId={id}
                canWrite={canWrite}
                composer={noteComposer}
              />

              <BuyingCommittee
                opportunityId={id}
                accountId={(record['account_id'] as string | null) ?? null}
                accountName={(record['account_name'] as string | null) ?? null}
                repFirmId={(record['rep_firm_id'] as string | null) ?? null}
                repFirmName={(record['rep_firm_name'] as string | null) ?? null}
                people={context.data?.people ?? []}
                canWrite={canWrite}
              />

              <ProductLines
                opportunityId={id}
                priceBasis={String(record['price_basis'])}
                canWrite={canWrite}
              />

              <section className="grid gap-3 sm:grid-cols-2">
                <Field label="Program">
                  <p className="text-sm">
                    {record['program_name'] === null || record['program_name'] === undefined ? (
                      '—'
                    ) : (
                      <Link
                        to={`/programs/${String(record['program_id'])}`}
                        className="hover:underline"
                      >
                        {String(record['program_name'])}
                      </Link>
                    )}
                  </p>
                </Field>
                <Field label="End product (what the customer is building)">
                  <InlineEdit
                    label="End product"
                    value={record['end_product'] as string}
                    disabled={!canWrite}
                    onSave={(next) => save.mutateAsync({ end_product: next })}
                  />
                </Field>
                <Field label="Application">
                  <InlineEdit
                    label="Application"
                    value={record['application'] as string}
                    disabled={!canWrite}
                    onSave={(next) => save.mutateAsync({ application: next })}
                  />
                </Field>
                <Field
                  label="End-use country"
                  hint="Recorded for context. This system screens nothing against it."
                >
                  <InlineEdit
                    label="End-use country"
                    value={record['end_use_country'] as string}
                    disabled={!canWrite}
                    placeholder="ISO alpha-2"
                    onSave={(next) =>
                      save.mutateAsync({ end_use_country: next?.toUpperCase() ?? null })
                    }
                  />
                </Field>
              </section>
            </div>
          </TabPanel>

          <TabPanel id="activity" active={tab}>
            <Timeline
              entries={context.data?.timeline ?? []}
              truncatedAt={context.data?.timelineTruncatedAt ?? null}
            />
          </TabPanel>

          <TabPanel id="actions" active={tab}>
            <ActionsTab
              openItems={openItems}
              canWrite={canWrite}
              quickAddButton={quickAdd.button}
            />
          </TabPanel>

          <TabPanel id="files" active={tab}>
            <AttachmentsPanel entityType="opportunity" entityId={id} canWrite={canWrite} />
          </TabPanel>

          <TabPanel id="history" active={tab}>
            <History opportunityId={id} closures={closures} />
          </TabPanel>
        </div>

        {/* --------------------------------------------------------- the rail */}
        <aside className="space-y-4">
          <section className="space-y-3 rounded-lg border bg-card p-4">
            <DualBasisRevenue
              customer={record['annual_revenue_customer'] as string}
              net={record['annual_revenue_net'] as string}
              basis={record['price_basis'] as string}
              netFactor={record['rep_firm_net_factor'] as string}
              repFirmName={record['rep_firm_name'] as string}
            />

            <dl className="space-y-1.5 text-sm">
              <Stat
                label="Lifetime value"
                value={formatMoney(record['lifetime_value'] as string)}
                hint="Annual net x program life. Excludes NRE and prototype revenue."
              />
              <ProgramLifeRow
                value={(record['program_years'] as number | null | undefined) ?? null}
                canWrite={canWrite}
                onSave={(years) => save.mutateAsync({ program_years: years })}
              />
              <Stat label="NRE" value={formatMoney(record['nre_amount'] as string)} />
              <Stat
                label="Prototype revenue"
                value={formatMoney(record['prototype_revenue_amount'] as string)}
              />
              <Stat
                label="Weighted"
                value={formatMoney(record['weighted_revenue'] as string)}
                hint="Effective probability x annual net."
              />
              <Stat
                label="Probability"
                value={`${String(record['effective_probability'])}%`}
                hint={
                  record['probability_override'] === null
                    ? 'From the stage default.'
                    : 'Overridden on this deal.'
                }
              />
              <Stat
                label="Gross margin"
                value={
                  record['gross_margin_pct'] === null || record['gross_margin_pct'] === undefined
                    ? '—'
                    : `${String(record['gross_margin_pct'])}%`
                }
              />
            </dl>

            <Field label="Forecast category" hint="Revenue timing, not deal outcome.">
              <InlineEdit
                label="Forecast category"
                kind="select"
                value={record['forecast_category'] as string}
                disabled={!canWrite}
                options={FORECAST_CATEGORIES.map((value) => ({
                  value,
                  label: humanize(value),
                }))}
                onSave={(next) => save.mutateAsync({ forecast_category: next })}
              />
            </Field>
          </section>

          <FlagsSection
            record={record}
            canSet={canSetFlags}
            onSave={(body) => save.mutateAsync(body)}
          />

          <section className="space-y-2 rounded-lg border bg-card p-4">
            <h2 className="text-sm font-medium">Dates</h2>
            <DateRow
              label="Created"
              value={record['created_date'] as string}
              canWrite={false}
              onSave={async () => undefined}
            />
            <DateRow
              label="Expected decision"
              value={record['expected_decision_date'] as string}
              canWrite={canWrite}
              onSave={(next) => save.mutateAsync({ expected_decision_date: next })}
            />
            <DateRow
              label="Expected first revenue"
              value={record['expected_first_revenue_date'] as string}
              canWrite={canWrite}
              onSave={(next) => save.mutateAsync({ expected_first_revenue_date: next })}
            />
            <DateRow
              label="Expected ramp"
              value={record['expected_ramp_date'] as string}
              canWrite={canWrite}
              onSave={(next) => save.mutateAsync({ expected_ramp_date: next })}
            />
            <DateRow
              label="Actual ramp"
              value={record['actual_ramp_date'] as string}
              canWrite={canWrite}
              onSave={(next) => save.mutateAsync({ actual_ramp_date: next })}
            />
            <p className="text-[11px] text-muted-foreground">
              The close date belongs to the closure, not to the deal — it is in History.
            </p>
          </section>

          {(record['rep_firm_id'] !== null || record['design_registration_ref'] !== null) && (
            <section className="space-y-1.5 rounded-lg border bg-card p-4 text-sm">
              <h2 className="text-sm font-medium">Channel</h2>
              <Stat label="Rep firm" value={String(record['rep_firm_name'] ?? '—')} />
              <Stat label="Net factor" value={String(record['rep_firm_net_factor'] ?? '—')} />
              <Stat
                label="DR number"
                value={String(record['design_registration_ref'] ?? '—')}
                hint="The distributor's design registration reference. Stored, not workflowed."
              />
            </section>
          )}

          <section className="space-y-2 rounded-lg border bg-card p-4">
            <h2 className="flex items-center gap-2 text-sm font-medium">
              Risk
              <Badge variant="outline">7 signals</Badge>
            </h2>
            <RiskPanel row={record} />
          </section>
        </aside>
      </div>

      {mover.dialogs}
      {quickAdd.dialog}

      {closing && (
        <CloseDialog
          opportunity={record}
          closures={closures}
          onClose={() => setClosing(false)}
          onClosed={() => {
            setClosing(false);
            void context.refetch();
          }}
        />
      )}

      {assigningFae && (
        <AssignFaeDialog
          opportunityId={id}
          currentFaeId={(record['fae_user_id'] as string | null) ?? null}
          users={reference.users}
          onClose={() => setAssigningFae(false)}
          onDone={() => {
            setAssigningFae(false);
            void context.refetch();
          }}
        />
      )}

      {loggingMeeting && (
        <MeetingDialog
          accountId={String(record['account_id'])}
          opportunityId={id}
          onClose={() => setLoggingMeeting(false)}
          onSaved={() => {
            setLoggingMeeting(false);
            // Meetings are listed on Activity and nowhere on Overview, so
            // staying put after a save looked like nothing happened. The
            // owner chose the jump over a meetings panel on Overview
            // (docs/bugs.md 2026-10-10, session 1 part 2). The dialog has
            // already invalidated the context, so the timeline re-reads.
            setTab('activity');
          }}
        />
      )}

      {notePrompt && (
        <Dialog
          open
          onClose={() => setNotePrompt(false)}
          size="sm"
          title="Stage changed — update the summary?"
          description="The pinned note is what the next person reads first."
          footer={
            <>
              <Button variant="ghost" onClick={() => setNotePrompt(false)}>
                Not now
              </Button>
              <Button
                onClick={() => {
                  setNotePrompt(false);
                  addNote();
                }}
              >
                Update it
              </Button>
            </>
          }
        >
          <p className="text-sm text-muted-foreground">
            Nothing is required. It takes ten seconds and saves the next person ten minutes.
          </p>
        </Dialog>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-2" title={hint}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * Program life, 1–10 years (`docs/bugs.md` session 2 part 1). Every deal has
 * one — 3 unless someone changed it — so there is no empty choice to pick. A
 * deal already carrying more than ten (the column allows 30) keeps its value
 * as an extra option rather than being shown as something it is not.
 */
function ProgramLifeRow({
  value,
  canWrite,
  onSave,
}: {
  value: number | null;
  canWrite: boolean;
  onSave: (years: number) => Promise<unknown>;
}): React.ReactElement {
  const label = (years: number): string => `${years} ${years === 1 ? 'year' : 'years'}`;
  const options: number[] = [...PROGRAM_LIFE_OPTIONS];
  if (value !== null && !options.includes(value)) options.push(value);

  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-xs text-muted-foreground">Program life</dt>
      <dd>
        {canWrite ? (
          <Select
            aria-label="Program life"
            className="h-7 w-28 text-sm"
            value={value === null ? '' : String(value)}
            onChange={(event) => {
              // The mutation layer toasts a failure; the refetch puts the value back.
              if (event.target.value !== '') {
                void onSave(Number(event.target.value)).catch(() => undefined);
              }
            }}
          >
            {value === null && (
              <option value="" disabled>
                —
              </option>
            )}
            {options.map((years) => (
              <option key={years} value={years}>
                {label(years)}
              </option>
            ))}
          </Select>
        ) : (
          <span className="tabular-nums">{value === null ? '—' : label(value)}</span>
        )}
      </dd>
    </div>
  );
}

/**
 * Focus and Verified (`docs/bugs.md` session 4, decisions 14/15).
 *
 * Everyone sees both boxes; only a Manager or an Admin can tick them, from
 * the same shared predicate the API refuses with — a User sees them disabled,
 * not hidden, so the flag still reads. Verified says who and when, which the
 * server stamps: the box sends `is_verified` and nothing else.
 */
function FlagsSection({
  record,
  canSet,
  onSave,
}: {
  record: Row;
  canSet: boolean;
  onSave: (body: Record<string, unknown>) => Promise<unknown>;
}): React.ReactElement {
  const focus = record['is_focus'] === true;
  const verifiedAt = (record['verified_at'] as string | null | undefined) ?? null;
  const verifiedBy = (record['verified_by_name'] as string | null | undefined) ?? null;
  const why = canSet ? undefined : 'Only a sales manager or an administrator can change this.';

  // The mutation layer toasts a failure; the refetch puts the box back.
  const save = (body: Record<string, unknown>): void => {
    void onSave(body).catch(() => undefined);
  };

  return (
    <section className="space-y-2 rounded-lg border bg-card p-4 text-sm">
      <h2 className="text-sm font-medium">Flags</h2>
      <label className="flex items-start gap-2" title={why}>
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={focus}
          disabled={!canSet}
          onChange={(event) => save({ is_focus: event.target.checked })}
        />
        <span>
          <span className="font-medium">Focus</span>
          <span className="block text-xs text-muted-foreground">{FOCUS_HELP_TEXT}</span>
        </span>
      </label>
      <label className="flex items-start gap-2" title={why}>
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={verifiedAt !== null}
          disabled={!canSet}
          onChange={(event) => save({ is_verified: event.target.checked })}
        />
        <span>
          <span className="font-medium">Verified</span>
          <span className="block text-xs text-muted-foreground">
            {verifiedAt === null
              ? VERIFIED_HELP_TEXT
              : `Verified by ${verifiedBy ?? 'an unknown user'} on ${formatDate(verifiedAt)}.`}
          </span>
        </span>
      </label>
    </section>
  );
}

function DateRow({
  label,
  value,
  canWrite,
  onSave,
}: {
  label: string;
  value: string | null;
  canWrite: boolean;
  onSave: (next: string | null) => Promise<unknown>;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      {canWrite ? (
        <InlineEdit label={label} kind="date" value={value} onSave={onSave} className="w-40" />
      ) : (
        <span className="text-sm">{formatDate(value)}</span>
      )}
    </div>
  );
}

function Timeline({
  entries,
  truncatedAt,
}: {
  entries: EntityContext['timeline'];
  truncatedAt: string | null;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      <ol className="space-y-2">
        {entries.map((entry, index) => (
          <li key={`${entry.at}-${index}`} className="flex gap-3 text-sm">
            <span className="w-40 shrink-0 text-xs text-muted-foreground">
              {formatDateTime(entry.at)}
            </span>
            <span className="min-w-0">
              <Badge variant="outline">{entry.source}</Badge> <span>{entry.summary}</span>
              {entry.actor_name !== null && (
                <span className="text-xs text-muted-foreground"> — {entry.actor_name}</span>
              )}
            </span>
          </li>
        ))}
        {entries.length === 0 && (
          <li className="text-xs text-muted-foreground">Nothing has happened yet.</li>
        )}
      </ol>
      {truncatedAt !== null && (
        <p className="text-xs text-muted-foreground">
          Older than {formatDateTime(truncatedAt)} is not shown.
        </p>
      )}
    </div>
  );
}

/** Grouped internal versus customer-owned — the distinction that shows where a deal is stuck. */
function ActionsTab({
  openItems,
  canWrite,
  quickAddButton,
}: {
  openItems: Row[];
  canWrite: boolean;
  quickAddButton: React.ReactElement;
}): React.ReactElement {
  const internal = openItems.filter((item) => item['owner_type'] !== 'customer');
  const customer = openItems.filter((item) => item['owner_type'] === 'customer');

  return (
    <div className="space-y-4">
      <div className="flex justify-end">{canWrite && quickAddButton}</div>

      <section>
        <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Ours ({internal.length})
        </h3>
        <div className="rounded-md border">
          {internal.map((item) => (
            <ActionItemRow
              key={String(item['id'])}
              item={item}
              canWrite={canWrite}
              showOpportunity={false}
            />
          ))}
          {internal.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">Nothing open.</p>
          )}
        </div>
      </section>

      <section>
        <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Waiting on the customer ({customer.length})
        </h3>
        <div className="rounded-md border">
          {customer.map((item) => (
            <ActionItemRow
              key={String(item['id'])}
              item={item}
              canWrite={canWrite}
              showOpportunity={false}
            />
          ))}
          {customer.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              Nothing is waiting on them.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

/**
 * History: the audit trail, in sentences, **plus the closure history**.
 *
 * A deal won and later re-closed as lost shows both, with dates and reasons.
 * That history is the reason §5.5 is a table rather than a set of columns —
 * with columns, the win would have been overwritten and the Q1 win rate would
 * have changed retroactively.
 */
function History({
  opportunityId,
  closures,
}: {
  opportunityId: string;
  closures: ClosureRow[];
}): React.ReactElement {
  const history = useApiQuery<{ items: Record<string, unknown>[] }>(
    ['opportunity-history', opportunityId],
    `/api/opportunities/${opportunityId}/history`,
  );

  return (
    <div className="space-y-6">
      {closures.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-medium">
            Closures {closures.length > 1 && <Badge variant="outline">{closures.length}</Badge>}
          </h3>
          <ul className="space-y-2">
            {closures.map((closure) => (
              <li key={closure.closure_id} className="rounded-md border p-3 text-sm">
                <p className="flex flex-wrap items-center gap-2">
                  <Badge variant={closure.outcome === 'won' ? 'success' : 'danger'}>
                    {humanize(closure.outcome)}
                  </Badge>
                  <span>{formatDate(closure.close_date)}</span>
                  <span className="text-muted-foreground">{closure.primary_reason_label}</span>
                  {closure.is_first_win && (
                    <Badge variant="outline">Counted as the design win</Badge>
                  )}
                  {closure.is_superseded_by_reopen && (
                    <Badge variant="outline">Superseded — the deal was reopened</Badge>
                  )}
                </p>
                {closure.actual_annual_revenue !== null && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formatMoney(closure.actual_annual_revenue)} on the Menlo net basis, at a net
                    factor of {closure.net_factor_at_close ?? '—'} — the factor in force when it
                    closed.
                  </p>
                )}
                {closure.lessons_learned !== null && (
                  <p className="mt-1 text-xs">{closure.lessons_learned}</p>
                )}
              </li>
            ))}
          </ul>
          {closures.length > 1 && (
            <p className="mt-2 text-xs text-muted-foreground">
              Every closure is kept. A win stays counted in the quarter it happened even if the deal
              was later re-closed as lost.
            </p>
          )}
        </section>
      )}

      <section>
        <h3 className="mb-2 text-sm font-medium">Changes</h3>
        <ul className="space-y-1.5 text-sm">
          {(history.data?.items ?? [])
            // A Verified tick writes two audit rows; `verified_at` tells it.
            .filter((entry) => describeAudit(entry) !== '')
            .map((entry, index) => (
              <li key={index} className="flex gap-3">
                <span className="w-40 shrink-0 text-xs text-muted-foreground">
                  {formatDateTime(entry['at'] as string)}
                </span>
                <span>
                  {describeAudit(entry)}
                  {entry['actor_name'] !== null && (
                    <span className="text-xs text-muted-foreground">
                      {' '}
                      — {String(entry['actor_name'])}
                    </span>
                  )}
                </span>
              </li>
            ))}
        </ul>
        <p className="mt-2 text-xs text-muted-foreground">
          The audit trail is append-only and kept for five years. Deleting a record does not remove
          its history from here.
        </p>
      </section>
    </div>
  );
}

function describeAudit(entry: Record<string, unknown>): string {
  const kind = String(entry['kind']);
  if (kind !== 'audit') return String(entry['summary']);
  const detail = (entry['detail'] ?? {}) as Record<string, unknown>;
  const field = detail['field_name'];
  if (field === null || field === undefined) return String(entry['summary']);
  const from = detail['old_value'];
  const to = detail['new_value'];
  // Session 4's flags read as what they are, not as column names.
  if (field === 'is_focus') return to === 'true' ? 'marked Focus' : 'cleared Focus';
  if (field === 'verified_at') return to === null ? 'cleared Verified' : 'marked Verified';
  if (field === 'verified_by') return '';
  if (from === null && to !== null) return `set ${humanize(String(field))} to ${String(to)}`;
  return `changed ${humanize(String(field))} from ${String(from ?? '—')} to ${String(to ?? '—')}`;
}

function AssignFaeDialog({
  opportunityId,
  currentFaeId,
  users,
  onClose,
  onDone,
}: {
  opportunityId: string;
  currentFaeId: string | null;
  users: { id: string; display_name: string }[];
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const [faeId, setFaeId] = useState(currentFaeId ?? '');

  const assign = useApiMutation<Record<string, unknown>, void>(
    () => ({
      path: `/api/opportunities/${opportunityId}/transfer`,
      method: 'POST',
      body: { fae_user_id: faeId === '' ? null : faeId },
    }),
    { onSuccess: onDone },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="Assign an FAE"
      description="An assigned FAE can write this deal and everything under it. The change is audited."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => assign.mutate()} disabled={assign.isPending}>
            Save
          </Button>
        </>
      }
    >
      <Field label="FAE">
        <Select value={faeId} onChange={(event) => setFaeId(event.target.value)}>
          <option value="">Unassigned</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.display_name}
            </option>
          ))}
        </Select>
      </Field>
      <p className="mt-2 text-xs text-muted-foreground">
        Assigning an FAE grants write access to this deal, so it goes through the same audited
        transfer route a change of owner does. Nobody can assign themselves onto a deal they cannot
        already write.
      </p>
    </Dialog>
  );
}

/**
 * `/opportunities/:id/:tab` — the shape the §8e digest emails.
 *
 * `opportunityActionsUrl` builds `/opportunities/<id>/actions`, and that link
 * has to land on the deal's Actions tab as the **first navigation after
 * login**. Without this route it matched the catch-all and dumped the reader
 * on the dashboard — which §8e calls the failure that makes people ignore the
 * email, and which is worse here than usual because the content policy already
 * denies them any way to triage from the inbox.
 *
 * It redirects rather than rendering, so the tab lives in one place: the query
 * string that `useTab` reads and that a user can copy out of the address bar.
 */
export function OpportunityTabRedirect(): React.ReactElement {
  const { id = '', tab = 'overview' } = useParams();
  return <Navigate to={`/opportunities/${id}?tab=${encodeURIComponent(tab)}`} replace />;
}
